import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BotonCentralita from '@/components/centralita/BotonCentralita';
import { pedirAtencion, useAtencion, type ContadoresMenu } from '@/lib/atencion';
import { desgloseAtencion } from '@/lib/dispositivos';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  Inbox,
  Building2,
  PenLine,
  CheckSquare,
  FileSignature,
  Settings,
  X,
  Moon,
  Sun,
  Receipt,
  FileText,
  Calculator,
  ChevronDown,
  BookOpen,
  UserCircle,
  BarChart3,
  Euro,
  Globe,
  Ticket,
  Workflow,
  DoorOpen,
  Mail,
  ShoppingBag,
  Wrench,
  PhoneCall,
  BedDouble,
  Star,
  Briefcase,
  UserSearch,
  CalendarRange,
  Cpu,
  Megaphone,
  Send,
  Contact,
  MessagesSquare,
  LayoutTemplate,
  Bot,
} from 'lucide-react';
import { classNames } from '@/lib/utils';

type Contador = keyof ContadoresMenu;

const SIN_CONTADORES: ContadoresMenu = {
  peticionesNuevas: 0, candidatosNuevos: 0, tareasAtencion: 0, dispositivosAtencion: 0, conversacionesSinLeer: 0,
};

interface ChildItem {
  name: string;
  href: string;
  icon: React.ElementType;
  /** Clave del contador que se pinta a la derecha, si lo tiene. */
  contador?: Contador;
}

interface NavItem {
  name: string;
  href?: string;
  icon: React.ElementType;
  children?: ChildItem[];
  /** Solo para entradas sin hijos: el contador a la derecha. */
  contador?: Contador;
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  {
    // Sin `href` propio a propósito: aquí cuelgan varios destinos distintos y el
    // padre solo agrupa. Con `href`, `isParentActive` mira la ruta del padre e
    // ignora a los hijos, así que no se resaltaría al estar en uno de ellos.
    //
    // Las rutas son /leads/propietarios y /leads/suscritos, y no /leads a secas
    // para propietarios: el resaltado del hijo usa `startsWith`, así que con
    // /leads los DOS se marcarían a la vez al estar en suscritos.
    name: 'Leads', icon: UserPlus,
    children: [
      { name: 'Propietarios', href: '/leads/propietarios', icon: UserPlus },
      { name: 'Suscritos', href: '/leads/suscritos', icon: Mail },
      { name: 'Peticiones', href: '/leads/peticiones', icon: Inbox, contador: 'peticionesNuevas' },
      { name: 'Tareas', href: '/leads/tareas', icon: CheckSquare, contador: 'tareasAtencion' },
      // Fases del pipeline, fuentes de captación, zonas, tipos de propiedad y
      // los puestos de la bolsa de empleo. Vive bajo Leads por historia: nació
      // siendo solo la configuración de la captación.
      { name: 'Configuración', href: '/settings', icon: Settings },
    ],
  },
  {
    name: 'Clientes', icon: Users,
    children: [
      { name: 'Propietarios', href: '/owners', icon: UserCircle },
      { name: 'Huéspedes', href: '/huespedes', icon: BedDouble },
      { name: 'Contratos', href: '/contracts', icon: FileSignature },
    ],
  },
  {
    // Campañas de correo tipo Mailchimp desde news.tusereno.es, a los
    // suscriptores confirmados y a los propietarios (PLAN-COMUNICACIONES.md).
    // Grupo sin `href`, como los demás; ninguna ruta hija es prefijo de otra.
    name: 'Comunicaciones', icon: Megaphone,
    children: [
      { name: 'Campañas', href: '/comunicaciones/campanas', icon: Send },
      // Respuestas por WhatsApp a las campañas, para contestarlas desde el CRM
      { name: 'Conversaciones', href: '/comunicaciones/conversaciones', icon: MessagesSquare, contador: 'conversacionesSinLeer' },
      // Plantillas de WhatsApp: crearlas desde aquí y mandarlas a revisión de Meta
      { name: 'Plantillas', href: '/comunicaciones/plantillas', icon: LayoutTemplate },
      { name: 'Contactos', href: '/comunicaciones/contactos', icon: Contact },
      { name: 'Ajustes', href: '/comunicaciones/ajustes', icon: Settings },
    ],
  },
  {
    // «Equipo» y no «Leads»: quien manda el currículum no es un cliente
    // potencial. Debajo va también la plantilla ya contratada, que es la
    // continuación natural de la bolsa de empleo.
    name: 'Equipo', icon: Briefcase,
    children: [
      { name: 'Candidatos', href: '/equipo/candidatos', icon: UserSearch, contador: 'candidatosNuevos' },
      // Los empleados siguen viviendo en la app de lavandería; aquí solo se
      // consultan. El icono es el que usa allí la sección Personal.
      { name: 'Personal', href: '/equipo/personal', icon: Users },
    ],
  },
  {
    // Sin `href` propio, como el resto de grupos: con él, `isParentActive` mira
    // la ruta del padre e ignora a los hijos, y el grupo no se resaltaría al
    // estar dentro de uno. Las dos rutas hijas no son prefijo una de otra, así
    // que el `startsWith` del resaltado las distingue.
    name: 'Propiedades', icon: Building2,
    children: [
      { name: 'Listings', href: '/properties', icon: Building2 },
      { name: 'Copy', href: '/copy', icon: PenLine },
    ],
  },
  // El inventario del hardware de las casas (smartlocks, openers, bridges,
  // keypads, bots, hubs…). Sección propia y no hija de Propiedades, por
  // decisión de Matías (PLAN-DISPOSITIVOS.md §12): las pestañas van dentro.
  { name: 'Dispositivos', href: '/dispositivos', icon: Cpu, contador: 'dispositivosAtencion' },
  {
    name: 'Analytics', icon: BarChart3,
    children: [
      { name: 'Finance', href: '/finance', icon: Euro },
      // Cómo se reserva (estacionalidad, antelación, canales, repetidores…)
      // mirando todo el histórico; Finance responde cuánto se facturó en un periodo.
      { name: 'Reservas', href: '/analytics/reservas', icon: CalendarRange },
      { name: 'Reseñas', href: '/reviews', icon: Star },
      { name: 'Aperturas', href: '/aperturas', icon: DoorOpen },
      { name: 'Llamadas', href: '/llamadas', icon: PhoneCall },
    ],
  },
  {
    name: 'Contabilidad', icon: BookOpen,
    children: [
      { name: 'Liquidaciones', href: '/liquidaciones', icon: Receipt },
      { name: 'Facturas', href: '/facturas', icon: FileText },
      { name: 'Servicios', href: '/contabilidad/servicios', icon: ShoppingBag },
      // Los gastos de mantenimiento de sereno_lavanderia, que ya entraban en
      // las liquidaciones; aquí se dan de alta y se corrigen sin salir del CRM.
      { name: 'Gastos', href: '/contabilidad/gastos', icon: Wrench },
    ],
  },
  { name: 'Calculadoras', href: '/calculadoras', icon: Calculator },
  {
    name: 'Tusereno.es', icon: Globe,
    children: [
      { name: 'Cupones', href: '/tusereno/cupones', icon: Ticket },
      // Las conversaciones con Serena, el asistente de la web (últimos 30 días).
      { name: 'Serena', href: '/tusereno/serena', icon: Bot },
      // Lo que la web lee del CRM: hoy, desde qué fecha la temporada está cerrada.
      { name: 'Configuración', href: '/tusereno/configuracion', icon: Settings },
    ],
  },
  { name: 'Automatizaciones', href: '/automatizaciones', icon: Workflow },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
}

export default function Sidebar({ open, onClose, theme, onToggleTheme }: SidebarProps) {
  const location = useLocation();

  /**
   * Peticiones de propietarios sin decidir.
   *
   * El aviso de una petición nueva llega por Telegram, así que quien entra al
   * CRM por su cuenta no tiene forma de saber que hay algo esperando. Este
   * número es esa señal.
   *
   * Se recuenta al montar y cada vez que se entra o se sale de la sección
   * —no en cada navegación— para que baje al decidir una petición sin
   * convertir el menú en un sondeo constante al servidor.
   */
  const enPeticiones = location.pathname.startsWith('/leads/peticiones');
  const enCandidatos = location.pathname.startsWith('/equipo/candidatos');
  const enTareas = location.pathname.startsWith('/leads/tareas');
  const enDispositivos = location.pathname.startsWith('/dispositivos');
  const enConversaciones = location.pathname.startsWith('/comunicaciones/conversaciones');
  // Una pantalla que cambia lo que cuentan los números (leer una conversación)
  // avisa con este evento, en vez de esperar a que se cambie de ruta
  const [recontar, setRecontar] = useState(0);
  useEffect(() => {
    const avisar = () => setRecontar(n => n + 1);
    window.addEventListener('sereno:contadores', avisar);
    return () => window.removeEventListener('sereno:contadores', avisar);
  }, []);
  // Todos salen de GET /api/atencion, lo mismo que pinta el bloque «Pendiente»
  // del Dashboard: así los dos números cuadran. Cada lectura tiene su respaldo
  // en el servidor, y si falla la petición entera se queda lo último que se supo.
  const { datos: atencion } = useAtencion();
  const contadores = atencion?.menu ?? SIN_CONTADORES;
  // El de Dispositivos suma seis cosas distintas; sin esto el número no dice
  // qué hay que ir a mirar.
  const desgloseDispositivos = desgloseAtencion(atencion?.dispositivos);

  // Al montar vale la caché corta del servidor (el Dashboard la pide a la vez);
  // al entrar o salir de una sección, o con el aviso, se pide fresca para que
  // el número baje en cuanto se ha resuelto algo.
  const montado = useRef(false);
  useEffect(() => {
    void pedirAtencion(montado.current);
    montado.current = true;
  }, [enPeticiones, enCandidatos, enTareas, enDispositivos, enConversaciones, recontar]);

  const etiquetaContador = (clave: Contador): string => {
    switch (clave) {
      case 'peticionesNuevas': return `${contadores.peticionesNuevas} sin decidir`;
      case 'candidatosNuevos': return `${contadores.candidatosNuevos} sin revisar`;
      case 'tareasAtencion': return `${contadores.tareasAtencion} vencidas o para hoy`;
      case 'dispositivosAtencion': return `${contadores.dispositivosAtencion} que reclaman atención`;
      case 'conversacionesSinLeer': return `${contadores.conversacionesSinLeer} con mensajes sin leer`;
    }
  };

  /** Lo que sale al pasar el ratón: la etiqueta y, si lo hay, el desglose. */
  const tooltipContador = (clave: Contador): string => {
    const base = etiquetaContador(clave);
    if (clave !== 'dispositivosAtencion' || desgloseDispositivos.length === 0) return base;
    return `${base}:\n${desgloseDispositivos.map((l) => `· ${l}`).join('\n')}`;
  };

  const getInitialExpanded = () => {
    const expanded = new Set<string>();
    navigation.forEach(item => {
      if (item.children) {
        const anyChildActive = item.children.some(c => location.pathname.startsWith(c.href));
        const parentActive = item.href && location.pathname.startsWith(item.href);
        if (anyChildActive || parentActive) expanded.add(item.name);
      }
    });
    return expanded;
  };

  const [expanded, setExpanded] = useState<Set<string>>(getInitialExpanded);

  const toggleExpanded = (name: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={onClose} />
      )}

      <aside className={classNames(
        'fixed top-0 left-0 h-full w-56 bg-sereno-dark text-white flex flex-col z-40 transition-transform duration-200',
        open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      )}>
        <div className="px-5 py-6 border-b border-white/10 flex items-center justify-between lg:justify-center">
          <img
            src="https://serenodocsstor.blob.core.windows.net/assets/sereno-neg.png"
            alt="Sereno"
            className="h-10"
          />
          <button onClick={onClose} className="lg:hidden text-white/60 hover:text-white p-1">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navigation.map(item => {
            const isExpanded = expanded.has(item.name);
            const isParentActive = item.href
              ? item.href === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.href)
              : item.children?.some(c => location.pathname.startsWith(c.href)) ?? false;

            if (item.children) {
              return (
                <div key={item.name}>
                  <button
                    onClick={() => toggleExpanded(item.name)}
                    className={classNames(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                      isParentActive
                        ? 'bg-white/15 text-white'
                        : 'text-white/60 hover:bg-white/10 hover:text-white'
                    )}
                  >
                    {item.href ? (
                      <Link
                        to={item.href}
                        onClick={e => { e.stopPropagation(); onClose(); }}
                        className="flex items-center gap-3 flex-1 min-w-0"
                      >
                        <item.icon size={18} className="flex-shrink-0" />
                        <span className="flex-1 text-left truncate">{item.name}</span>
                      </Link>
                    ) : (
                      <>
                        <item.icon size={18} className="flex-shrink-0" />
                        <span className="flex-1 text-left truncate">{item.name}</span>
                      </>
                    )}
                    <ChevronDown
                      size={14}
                      className={classNames('flex-shrink-0 transition-transform duration-200', isExpanded ? 'rotate-180' : '')}
                    />
                  </button>

                  {isExpanded && (
                    <div className="mt-0.5 space-y-0.5">
                      {item.children.map(child => {
                        const isChildActive = location.pathname.startsWith(child.href);
                        return (
                          <Link
                            key={child.href}
                            to={child.href}
                            onClick={onClose}
                            className={classNames(
                              'flex items-center gap-3 pl-9 pr-3 py-2 rounded-lg text-sm font-medium transition-colors',
                              isChildActive
                                ? 'bg-white/10 text-white'
                                : 'text-white/40 hover:bg-white/10 hover:text-white/80'
                            )}
                          >
                            <child.icon size={15} />
                            <span className="flex-1 truncate">{child.name}</span>
                            {child.contador && contadores[child.contador] > 0 && (
                              <span
                                className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-white/20 text-white text-xs font-semibold flex items-center justify-center"
                                aria-label={tooltipContador(child.contador)}
                                title={tooltipContador(child.contador)}
                              >
                                {contadores[child.contador]}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.href}
                to={item.href!}
                onClick={onClose}
                className={classNames(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isParentActive
                    ? 'bg-white/15 text-white'
                    : 'text-white/60 hover:bg-white/10 hover:text-white'
                )}
              >
                <item.icon size={18} />
                <span className="flex-1 truncate">{item.name}</span>
                {item.contador && contadores[item.contador] > 0 && (
                  <span
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-white/20 text-white text-xs font-semibold flex items-center justify-center"
                    aria-label={tooltipContador(item.contador)}
                    title={tooltipContador(item.contador)}
                  >
                    {contadores[item.contador]}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-1">
          {/* Fuera de <nav>: no es una sección del CRM que consultar, es la
              herramienta con la que se llama. Separarlo lo dice sin explicarlo. */}
          <BotonCentralita onNavegar={onClose} />
          <button
            onClick={onToggleTheme}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/60 hover:bg-white/10 hover:text-white transition-colors w-full"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            {theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          </button>
        </div>
      </aside>
    </>
  );
}
